import * as THREE from 'three';

import { CLOUD_GLSL, CLOUD_HEIGHT, CLOUD_SCALE, CLOUD_THICKNESS } from './CloudNoise';
import { SkyState } from './Daylight';
import { WeatherState } from './Weather';

/**
 * Chmury: warstwa nad światem i jej cień na ziemi.
 *
 * Jedno i drugie pyta **tej samej funkcji szumu** (`CloudNoise`), przesuniętej
 * o to samo. Bez tego cień nie należałby do chmury, którą widać — a to jedyna
 * rzecz, na której ta iluzja stoi.
 *
 * **Cień jest liczony wstecz po promieniu słońca.** Punkt gruntu jest w cieniu
 * tej chmury, która leży nad nim **w stronę słońca**, o tyle w bok, ile wynosi
 * wysokość warstwy podzielona przez wysokość słońca nad horyzontem. Dlatego
 * o poranku cienie chmur są daleko od nich, a w południe leżą pod nimi — i to
 * wychodzi z arytmetyki, a nie z osobnego pokrętła.
 *
 * **Płaszczyzna jedzie za punktem skupienia**, bo świat jest strumieniowany
 * i nie ma sensu rozciągać jej na całą mapę. Jej brzegu nie widać, bo chmury
 * gasną promieniowo, zanim do niego dojdą — tym samym chwytem, którym mgła
 * chowa krawędź wczytanego terenu.
 */

/** Jak duża jest płaszczyzna. Dwa trójkąty, więc hojnie. */
const PLANE = 520;

/**
 * Gdzie chmury zaczynają gasnąć i gdzie znikają, licząc od punktu skupienia.
 *
 * Wewnątrz mgły, żeby brzeg płaszczyzny nigdy nie wypadł przed nią.
 */
const FADE_FROM = 120;
const FADE_TO = 190;

/** Słońce tuż nad horyzontem rzucałoby cień w nieskończoność. */
const MIN_SUN_HEIGHT = 0.22;

/**
 * Ile kroków robi promień przez płytę i ile w stronę słońca.
 *
 * **To są dwie najdroższe liczby w całym rendererze i dlatego stoją tutaj.**
 * Każdy krok marszu to jedno pytanie o gęstość (dwie oktawy szumu), a każdy
 * krok światła — kolejne, i to dla każdego kroku marszu, który w czymkolwiek
 * siedzi. Dwanaście na cztery jednostki grubości daje próbkę co pół jednostki,
 * czyli co heks; rzadziej widać pasy, gęściej nie widać różnicy w buforze, który
 * i tak jest pikselowy.
 *
 * **Jeśli to okaże się za drogie, następnym krokiem nie jest ścinanie tych
 * liczb, tylko osobny bufor w połowie rozdzielczości** — chmury są jedyną rzeczą
 * w kadrze, której rozmycie nie przeszkadza, a marsz liczony na ćwierci pikseli
 * kosztuje ćwierć. Zmiana zamknęłaby się w tym pliku.
 */
const MARCH_STEPS = 12;
const LIGHT_STEPS = 3;

/**
 * Jak mocno gęstość pochłania światło po drodze do oka i w stronę słońca.
 *
 * Prawo Beera: przepuszczalność to `exp(-gęstość · droga · pochłanianie)`.
 * Pierwsza liczba decyduje, jak szybko chmura robi się nieprzezroczysta, druga
 * — jak ciemne są jej spody. Druga jest większa, bo droga w stronę słońca jest
 * krótka i bez tego chmura byłaby równomiernie jasna, czyli płaska.
 */
const ABSORB_VIEW = 1.35;
const ABSORB_SUN = 2.6;

/**
 * Od jakiego przybliżenia kamera przechodzi przez chmury i przy jakim jest już
 * pod nimi.
 *
 * Zoom chodzi od 1 do 5,5, a **od 2 w górę chmur nie widać wcale** — warstwa nad
 * światem należy do widoku z daleka, gdzie ogląda się krajobraz. Bliżej patrzy
 * się na pole i to, co na nim stoi, a chmura jest wtedy wyłącznie czymś, co
 * zasłania. Nie jest to sztuczka na czytelność: kamera zeszła niżej, chmury
 * zostały nad nią.
 *
 * **Warto wiedzieć, że domyślne przybliżenie to 2**, czyli wchodząc do świata
 * chmur się nie widzi — trzeba oddalić. To jest ta cena i jest zamierzona.
 *
 * **Cień na ziemi zostaje.** Zejście pod chmurę nie usuwa jej ze świata, tylko
 * z kadru; ziemia dalej jest w cieniu, a cień niczego nie zasłania — tak samo
 * jak cień drzewa nie znika, gdy podejść bliżej.
 */
const THROUGH_FROM = 1.5;
const THROUGH_TO = 2;

export interface CloudUniforms {
  uCloudOffset: { value: THREE.Vector2 };
  uCloudScale: { value: number };
  uCloudCover: { value: number };
  /** Ile cienia kłaść na grunt: 0 w nocy i przy wyłączonych chmurach. */
  uCloudShadow: { value: number };
  /** O ile w bok leży chmura rzucająca cień na dany punkt gruntu. */
  uCloudSunOffset: { value: THREE.Vector2 };
}

export function createCloudUniforms(): CloudUniforms {
  return {
    uCloudOffset: { value: new THREE.Vector2() },
    uCloudScale: { value: CLOUD_SCALE },
    uCloudCover: { value: 0 },
    uCloudShadow: { value: 0 },
    uCloudSunOffset: { value: new THREE.Vector2() },
  };
}

/**
 * Dołóż cień chmur do materiału, który już istnieje.
 *
 * Owija poprzednie `onBeforeCompile`, a nie podmienia go: woda ma własne i
 * podmienienie zabrałoby jej falowanie. To jest ta pułapka, którą łatwo tu
 * przegapić, bo materiał kompiluje się bez słowa i po prostu przestaje robić
 * połowę tego, co robił.
 */
export function applyCloudShadow(material: THREE.Material, uniforms: CloudUniforms): void {
  const before = material.onBeforeCompile;

  material.onBeforeCompile = (shader, renderer) => {
    before?.call(material, shader, renderer);

    shader.uniforms.uCloudOffset = uniforms.uCloudOffset;
    shader.uniforms.uCloudScale = uniforms.uCloudScale;
    shader.uniforms.uCloudCover = uniforms.uCloudCover;
    shader.uniforms.uCloudShadow = uniforms.uCloudShadow;
    shader.uniforms.uCloudSunOffset = uniforms.uCloudSunOffset;

    // A varying of our own rather than three's `worldPosition`, which only
    // exists when something else in the shader happened to ask for it.
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vCloudWorld;')
      .replace(
        '#include <begin_vertex>',
        '#include <begin_vertex>\nvCloudWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;'
      );

    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec3 vCloudWorld;
        uniform vec2 uCloudOffset;
        uniform float uCloudScale;
        uniform float uCloudCover;
        uniform float uCloudShadow;
        uniform vec2 uCloudSunOffset;
        ${CLOUD_GLSL}`
      )
      // Last, after the light and the fog have had their say: a cloud passing
      // over darkens what is already there rather than changing how it is lit.
      .replace(
        '#include <dithering_fragment>',
        `#include <dithering_fragment>
        if (uCloudShadow > 0.0) {
          float overhead = cloudAt(
            vCloudWorld.xz + uCloudSunOffset, uCloudOffset, uCloudScale, uCloudCover
          );
          gl_FragColor.rgb *= 1.0 - overhead * uCloudShadow;
        }`
      );
  };

  material.needsUpdate = true;
}

export class Clouds {
  readonly object: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  constructor(private readonly uniforms: CloudUniforms) {
    this.material = new THREE.ShaderMaterial({
      transparent: true,
      // Seen from underneath, and never written to the depth buffer: it hangs
      // over everything and must not stop anything behind it being drawn.
      side: THREE.DoubleSide,
      depthWrite: false,
      uniforms: {
        uCloudOffset: uniforms.uCloudOffset,
        uCloudScale: uniforms.uCloudScale,
        uCloudCover: uniforms.uCloudCover,
        uCloudColor: { value: new THREE.Color(0xffffff) },
        /** Barwa spodu kłębu: to, czego światło do niego nie dosięgło. */
        uCloudShade: { value: new THREE.Color(0x6a7280) },
        uCloudOpacity: { value: 0 },
        uCloudFocus: { value: new THREE.Vector2() },
        /**
         * Kierunek patrzenia kamery, jeden na cały kadr.
         *
         * **Rzut jest ortograficzny i to jest tu prezent**: przy perspektywie
         * każdy piksel miałby własny promień, liczony z pozycji kamery; tutaj
         * wszystkie są równoległe, więc kierunek jedzie uniformem i nie ma
         * w shaderze ani jednego dzielenia na piksel.
         */
        uViewDir: { value: new THREE.Vector3(0, -1, 0) },
        /** Dokąd iść po światło. Kierunek **do** słońca, nie promienia. */
        uSunDir: { value: new THREE.Vector3(0, 1, 0) },
      },
      vertexShader: /* glsl */ `
        varying vec3 vCloudWorld;
        void main() {
          vCloudWorld = (modelMatrix * vec4(position, 1.0)).xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vCloudWorld;
        uniform vec2 uCloudOffset;
        uniform float uCloudScale;
        uniform float uCloudCover;
        uniform vec3 uCloudColor;
        uniform vec3 uCloudShade;
        uniform float uCloudOpacity;
        uniform vec2 uCloudFocus;
        uniform vec3 uViewDir;
        uniform vec3 uSunDir;
        ${CLOUD_GLSL}

        /**
         * Ile światła dochodzi do tego punktu w chmurze.
         *
         * Kilka kroków w stronę słońca i prawo Beera na tym, co po drodze.
         * Krótko i grubo: chodzi o to, żeby szczyt kłębu był jaśniejszy od jego
         * spodu, a nie o poprawny transport światła.
         */
        float lightReaching(vec3 at, float step) {
          float blocked = 0.0;
          for (int i = 1; i <= ${LIGHT_STEPS}; i++) {
            vec3 p = at + uSunDir * (step * float(i));
            blocked += cloudDensity(p, uCloudOffset, uCloudScale, uCloudCover);
          }
          return exp(-blocked * step * ${ABSORB_SUN});
        }

        void main() {
          // Promień wchodzi górą płyty, a nie w punkcie płaszczyzny: ta leży
          // w jej **środku** i jest wyłącznie powierzchnią, na której ten
          // fragment się liczy. Rzut jest ortograficzny, więc kierunek jest
          // jeden dla całego kadru i przyjeżdża uniformem.
          float halfSlab = ${CLOUD_THICKNESS}.0 * 0.5;
          // Pion kierunku przycięty: przy kamerze patrzącej prawie poziomo
          // droga przez płytę uciekałaby w nieskończoność.
          float down = max(abs(uViewDir.y), 0.25);
          vec3 entry = vCloudWorld - uViewDir * (halfSlab / down);

          float march = (${CLOUD_THICKNESS}.0 / down) / float(${MARCH_STEPS});
          float lightStep = ${CLOUD_THICKNESS}.0 / float(${LIGHT_STEPS} + 1);

          float transmittance = 1.0;
          vec3 scattered = vec3(0.0);

          for (int i = 0; i < ${MARCH_STEPS}; i++) {
            vec3 at = entry + uViewDir * (march * (float(i) + 0.5));
            float density = cloudDensity(at, uCloudOffset, uCloudScale, uCloudCover);
            if (density <= 0.001) continue;

            // Światło pytane tylko tam, gdzie jest co oświetlić — to jest ta
            // jedna gałąź, która trzyma koszt w ryzach przy czystym niebie.
            float lit = lightReaching(at, lightStep);
            vec3 colour = mix(uCloudShade, uCloudColor, lit);

            float taken = density * march * ${ABSORB_VIEW};
            float absorbed = 1.0 - exp(-taken);
            scattered += colour * absorbed * transmittance;
            transmittance *= 1.0 - absorbed;

            // Dalej jest już tylko chmura za chmurą, której nie widać.
            if (transmittance < 0.02) break;
          }

          float away = distance(vCloudWorld.xz, uCloudFocus);
          float near = 1.0 - smoothstep(${FADE_FROM}.0, ${FADE_TO}.0, away);
          float alpha = (1.0 - transmittance) * near * uCloudOpacity;
          if (alpha < 0.004) discard;

          // Kolor dzielony przez krycie, bo scattered jest już przemnożone
          // przez to, ile światła z każdego kroku wyszło — a three oczekuje
          // barwy nieprzemnożonej.
          gl_FragColor = vec4(scattered / max(1.0 - transmittance, 0.001), alpha);
        }
      `,
    });

    const plane = new THREE.PlaneGeometry(PLANE, PLANE);
    plane.rotateX(-Math.PI / 2);
    this.object = new THREE.Mesh(plane, this.material);
    this.object.frustumCulled = false;
    this.object.renderOrder = 5;
    this.object.position.y = CLOUD_HEIGHT;
  }

  set visible(value: boolean) {
    this.object.visible = value;
  }

  get visible(): boolean {
    return this.object.visible;
  }

  /**
   * Jedna klatka pogody.
   *
   * `reveal` mnoży przezroczystość, bo chmury nie mogą wyjść przed świat —
   * mgła otwierająca grę zakrywa teren, a warstwa nad nią wisiałaby nad niczym.
   */
  update(
    weather: WeatherState,
    sky: SkyState,
    focus: THREE.Vector3,
    reveal: number,
    zoom: number,
    camera: THREE.Camera
  ): void {
    this.object.position.x = focus.x;
    this.object.position.z = focus.z;

    this.uniforms.uCloudOffset.value.set(weather.offsetX, weather.offsetZ);
    this.uniforms.uCloudCover.value = weather.cover;

    const uniforms = this.material.uniforms;
    uniforms.uCloudFocus.value.set(focus.x, focus.z);

    // Kierunek patrzenia — jeden na cały kadr, bo rzut jest ortograficzny.
    // Czytany z kamery co klatkę, a nie zapamiętany: obrót rzutu jest sterowaniem
    // gracza, więc zmienia się wtedy, kiedy on chce.
    camera.getWorldDirection(uniforms.uViewDir.value as THREE.Vector3);
    // `sky.direction` wskazuje **w stronę** źródła światła — to samo, czego
    // używa cień na ziemi, i to jest ten sam kierunek, którym idzie się po
    // światło wewnątrz chmury.
    (uniforms.uSunDir.value as THREE.Vector3).copy(sky.direction).normalize();
    const through = 1 - THREE.MathUtils.smoothstep(zoom, THROUGH_FROM, THROUGH_TO);
    uniforms.uCloudOpacity.value = this.object.visible ? 0.85 * reveal * through : 0;
    // Lit by whatever is up: the sun's own colour by day, the cold half of the
    // ambient term at night, so clouds are not white cut-outs on a dark sky.
    uniforms.uCloudColor.value
      .copy(sky.night ? sky.skyColor : sky.sunColor)
      .lerp(sky.fogColor, 0.35);
    // Spód kłębu: ta sama barwa, tylko przygaszona. Osobny, zimniejszy kolor
    // byłby drugim pokrętłem do stroju nieba — a chmura ma być z tego samego
    // światła, co wszystko inne, tylko go nie dostaje.
    uniforms.uCloudShade.value
      .copy(sky.fogColor)
      .lerp(sky.skyColor, 0.4)
      .multiplyScalar(0.55);

    // Where the cloud that shadows a point of ground actually is. The light
    // travels along −direction, so going back up to the layer means going
    // along +direction, scaled by how far it has to climb.
    const height = Math.max(sky.direction.y, MIN_SUN_HEIGHT);
    const reach = CLOUD_HEIGHT / height;
    this.uniforms.uCloudSunOffset.value.set(
      sky.direction.x * reach,
      sky.direction.z * reach
    );
    this.uniforms.uCloudShadow.value = this.object.visible
      ? 0.45 * sky.shadowStrength * reveal
      : 0;
  }

  dispose(): void {
    this.object.geometry.dispose();
    this.material.dispose();
  }
}
